import React, { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Button, Icon, T, Table, VStack, HStack } from 'govwell-ui'
import { AICommunityAssistantUsageTier } from 'types/graphql'

// Comparison-matrix pricing layout used for orgs on the new pricing
// structure (AicaReverseTrial / AicaLite / AicaPremium / AicaPro /
// AicaEnterprise). Built on govwell-ui Table — each row is a
// `MatrixRow` whose four tier fields hold the cell content (text for
// header-style rows, check/x icons for feature rows, formatted price /
// Button for the bottom rows). Premium gets a continuous amber band
// via Table's per-column `bodyCellClassName` / `headerCellClassName`
// meta. Non-Premium columns set `headerCellClassName` to
// `bg-transparent` so the table's default `bg-neutral-100` header
// strip is dropped on this marketing layout — twMerge resolves the
// conflicting backgrounds in favor of the per-column override. Legacy
// orgs (AicaLegacyTrial) continue to render the older card-grid
// layout via the modal's branch.

// Hand-curated rather than derived from `AIResidentAssistantUsageTier`
// on purpose: the matrix is a marketing layout that intentionally only
// renders the four publicly-priced tiers. Trial / disabled / legacy tiers
// are never columns here even though they're valid backend enum values,
// so deriving (e.g. via `Exclude<...>`) would be misleading and would
// add a column automatically the next time the enum grows. If a new
// public tier is ever introduced, COLUMNS, FEATURES.includedIn, and this
// union must all be updated together.
type ColumnTierId = 'AicaLite' | 'AicaPremium' | 'AicaPro' | 'AicaEnterprise'

type PricingColumn = {
  tierId: ColumnTierId
  name: string
  averageMonthlyQueriesText: string
  priceText: string
  priceSubtext?: string
  ctaText: string
  isMostPopular?: boolean
}

const COLUMNS: PricingColumn[] = [
  {
    tierId: 'AicaLite',
    name: 'Lite',
    averageMonthlyQueriesText: 'Up to 100',
    priceText: 'Free',
    ctaText: 'Get started',
  },
  {
    tierId: 'AicaPremium',
    name: 'Premium',
    averageMonthlyQueriesText: 'Up to 500',
    priceText: '$5,000',
    priceSubtext: '/ yr',
    ctaText: 'Upgrade',
    isMostPopular: true,
  },
  {
    tierId: 'AicaPro',
    name: 'Pro',
    averageMonthlyQueriesText: 'Up to 1,000',
    priceText: '$9,000',
    priceSubtext: '/ yr',
    ctaText: 'Upgrade',
  },
  {
    tierId: 'AicaEnterprise',
    name: 'Enterprise',
    averageMonthlyQueriesText: '1,000+',
    priceText: 'Custom',
    ctaText: 'Contact sales',
  },
]

type FeatureRow = {
  title: string
  description: string
  // Which tier columns include this feature. Indexed by ColumnTierId.
  includedIn: Record<ColumnTierId, boolean>
}

const FEATURES: FeatureRow[] = [
  {
    title: '24/7 AI chat support',
    description: 'AI assistant on government portal and within GovWell app at all hours',
    includedIn: { AicaLite: true, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: 'Trained on your unique data',
    description: 'AI assistant makes answers specific to your agency',
    includedIn: { AicaLite: true, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: 'Website widget',
    description: 'Embedded directly on your website',
    includedIn: { AicaLite: true, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: '80+ languages',
    description: 'Residents can ask questions in 80+ supported languages',
    includedIn: { AicaLite: true, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: 'Custom routing and answers',
    description:
      'Route questions to the right staff member and set approved responses for specific queries',
    includedIn: { AicaLite: false, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: 'Advanced analytics',
    description: 'Proactive insights based on usage, topics, and performance',
    includedIn: { AicaLite: false, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: 'Deployment support',
    description: 'Hands-on setup and guidance from the GovWell team',
    includedIn: { AicaLite: false, AicaPremium: true, AicaPro: true, AicaEnterprise: true },
  },
  {
    title: 'Custom branding',
    description: 'Change the colors and style of your Assistant for your website',
    includedIn: { AicaLite: false, AicaPremium: false, AicaPro: false, AicaEnterprise: true },
  },
  {
    title: 'Custom tone',
    description: "Adjust the assistant's tone to match your communication style",
    includedIn: { AicaLite: false, AicaPremium: false, AicaPro: false, AicaEnterprise: true },
  },
  {
    title: 'Voice mode',
    description: 'Let residents interact using speech instead of typing',
    includedIn: { AicaLite: false, AicaPremium: false, AicaPro: false, AicaEnterprise: true },
  },
]

type Props = {
  tiers: AICommunityAssistantUsageTier[]
  handleNotifySalesTeam: (tier: AICommunityAssistantUsageTier) => Promise<void>
}

const FeatureCheck = () => <Icon.FaCheck className="text-emerald-600" aria-label="Included" />

const FeatureCross = () => <Icon.FaXmark className="text-red-700" aria-label="Not included" />

type RowKind = 'header' | 'feature' | 'price' | 'cta'

type MatrixRow = {
  id: string
  kind: RowKind
  feature: React.ReactNode
} & Record<ColumnTierId, React.ReactNode>

type ColumnHeaderProps = {
  column: PricingColumn
  isCurrent: boolean
}

// VStack's `gap` prop only accepts integers (0-10 → multiples of 4px).
// To approximate `gap={1.5}` (6px) we use `gap={1}` (4px) plus a 2px
// top margin on the tier name. Outer `py-1` adds 4px above/below the
// whole header block.
const TierColumnHeader = ({ column, isCurrent }: ColumnHeaderProps) => (
  <VStack align="center" gap={1} className="w-full px-1 py-1">
    <HStack align="center" justify="center" className="h-4 w-full">
      {column.isMostPopular && (
        <span className="inline-block rounded bg-amber-200 px-1.5 py-px text-xs font-semibold text-amber-900">
          Most popular
        </span>
      )}
      {!column.isMostPopular && isCurrent && (
        <span className="inline-block rounded bg-blue-100 px-1.5 py-px text-xs font-semibold text-blue-800">
          Current plan
        </span>
      )}
    </HStack>
    <T.BodyLarge className="mt-0.5 text-2xl font-semibold text-neutral-900">
      {column.name}
    </T.BodyLarge>
  </VStack>
)

type CtaButtonProps = {
  column: PricingColumn
  matchingTier: AICommunityAssistantUsageTier | undefined
  handleNotifySalesTeam: (tier: AICommunityAssistantUsageTier) => Promise<void>
}

const CtaButton = ({ column, matchingTier, handleNotifySalesTeam }: CtaButtonProps) => {
  const isDisabled = !matchingTier || (matchingTier.isDisabled ?? false)
  const isCurrent = matchingTier?.isCurrent ?? false

  const handleClick = useCallback(async () => {
    if (isDisabled || !matchingTier) {
      return
    }
    await handleNotifySalesTeam(matchingTier)
  }, [isDisabled, matchingTier, handleNotifySalesTeam])

  if (isCurrent) {
    return (
      <Button variant="default" isDisabled className="w-full">
        Current plan
      </Button>
    )
  }

  return (
    <Button
      variant={column.isMostPopular ? 'primary' : 'default'}
      isDisabled={isDisabled}
      onClick={handleClick}
      className="w-full"
    >
      {column.ctaText}
    </Button>
  )
}

const getRowId = (row: MatrixRow) => row.id

const AIAssistantUpsellPricingMatrix = ({ tiers, handleNotifySalesTeam }: Props) => {
  const tiersById = useMemo(() => {
    const map = new Map<string, AICommunityAssistantUsageTier>()
    for (const tier of tiers) {
      map.set(tier.id, tier)
    }
    return map
  }, [tiers])

  const data = useMemo<MatrixRow[]>(() => {
    const queriesRow: MatrixRow = {
      id: 'avg-monthly-queries',
      kind: 'header',
      feature: <T.Body className="font-semibold text-neutral-900">Average monthly queries</T.Body>,
      AicaLite: '',
      AicaPremium: '',
      AicaPro: '',
      AicaEnterprise: '',
    }
    for (const column of COLUMNS) {
      queriesRow[column.tierId] = (
        <T.Body className="font-semibold text-neutral-900">
          {column.averageMonthlyQueriesText}
        </T.Body>
      )
    }

    const featureRows: MatrixRow[] = FEATURES.map((feature) => {
      const row: MatrixRow = {
        id: `feature-${feature.title}`,
        kind: 'feature',
        feature: (
          <VStack align="start" gap={0}>
            <T.Body className="font-semibold text-neutral-900">{feature.title}</T.Body>
            <T.BodySmall className="text-neutral-600">{feature.description}</T.BodySmall>
          </VStack>
        ),
        AicaLite: '',
        AicaPremium: '',
        AicaPro: '',
        AicaEnterprise: '',
      }
      for (const column of COLUMNS) {
        row[column.tierId] = feature.includedIn[column.tierId] ? <FeatureCheck /> : <FeatureCross />
      }
      return row
    })

    const priceRow: MatrixRow = {
      id: 'price',
      kind: 'price',
      feature: <T.Body className="font-semibold text-neutral-900">Price</T.Body>,
      AicaLite: '',
      AicaPremium: '',
      AicaPro: '',
      AicaEnterprise: '',
    }
    for (const column of COLUMNS) {
      priceRow[column.tierId] = (
        <HStack align="baseline" justify="center" gap={1}>
          <T.BodyLarge className="text-xl font-semibold text-neutral-900">
            {column.priceText}
          </T.BodyLarge>
          {column.priceSubtext && (
            <T.BodySmall className="text-neutral-600">{column.priceSubtext}</T.BodySmall>
          )}
        </HStack>
      )
    }

    const ctaRow: MatrixRow = {
      id: 'cta',
      kind: 'cta',
      feature: '',
      AicaLite: '',
      AicaPremium: '',
      AicaPro: '',
      AicaEnterprise: '',
    }
    for (const column of COLUMNS) {
      ctaRow[column.tierId] = (
        <CtaButton
          column={column}
          matchingTier={tiersById.get(column.tierId)}
          handleNotifySalesTeam={handleNotifySalesTeam}
        />
      )
    }

    return [queriesRow, ...featureRows, priceRow, ctaRow]
  }, [tiersById, handleNotifySalesTeam])

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<MatrixRow>()
    // `!py-1.5` overrides the Table's default 8px vertical cell padding
    // (the styled-component sets `padding: 8px` shorthand, so we need
    // `!important` via `!` to win the cascade) and lands at 6px — a
    // middle ground between the default 8px and the tighter 4px (`!py-1`).
    const denseRowPadding = '!py-1.5'
    return [
      columnHelper.accessor('feature', {
        id: 'feature',
        header: '',
        meta: {
          width: '42%',
          bodyCellClassName: denseRowPadding,
          // Drop the default `bg-neutral-100` header strip on this
          // marketing layout (twMerge resolves conflicting `bg-*`).
          headerCellClassName: `bg-transparent ${denseRowPadding}`,
        },
        cell: ({ getValue }) => getValue(),
      }),
      ...COLUMNS.map((column) =>
        columnHelper.accessor(column.tierId, {
          id: column.tierId,
          header: () => (
            <TierColumnHeader
              column={column}
              isCurrent={tiersById.get(column.tierId)?.isCurrent ?? false}
            />
          ),
          meta: {
            width: '14.5%',
            textAlign: 'center',
            // Premium gets the continuous amber band; the other columns
            // clear the table's default `bg-neutral-100` header strip so
            // this marketing layout reads as a single block of content
            // (twMerge collapses conflicting `bg-*` utilities).
            bodyCellClassName: column.isMostPopular
              ? `bg-amber-50 ${denseRowPadding}`
              : denseRowPadding,
            headerCellClassName: column.isMostPopular
              ? `bg-amber-50 ${denseRowPadding}`
              : `bg-transparent ${denseRowPadding}`,
          },
          cell: ({ getValue }) => getValue(),
        })
      ),
    ]
  }, [tiersById])

  return (
    <VStack align="start" gap={2} className="w-full">
      <VStack align="start" gap={1}>
        <T.BodySmall className="font-semibold tracking-widest text-neutral-500 uppercase">
          Community Assistant
        </T.BodySmall>
        <T.BodyLarge className="text-2xl leading-tight font-semibold text-neutral-900">
          Pricing that scales with your community
        </T.BodyLarge>
        <T.BodySmall className="text-neutral-600">
          Billed annually with your price locked in for the year. Pricing is based on historical
          usage. Upgrade early to lock in a lower rate as your volume grows.
        </T.BodySmall>
      </VStack>

      <Table<MatrixRow>
        columns={columns}
        data={data}
        getRowId={getRowId}
        minWidth="900px"
        verticalAlign="middle"
      />
    </VStack>
  )
}

export default React.memo(AIAssistantUpsellPricingMatrix)
